import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { makeAskStreamSynthesisSession } from "../../domain/use-cases/ask-stream-synthesis-session"
import { httpSynthesisSessionRepository } from "../../data/repositories/synthesis-session-repository"
import type {
  AskStreamEvent,
  AskStreamPhase,
  ProcessingStep,
  ProcessingStepInfo,
  SynthesisAskResult,
  SynthesisSession,
} from "../../domain/entities/synthesis-session"

const askStreamApi = makeAskStreamSynthesisSession(httpSynthesisSessionRepository)

const sessionKey = (sessionId: string) => ["synthesis-sessions", sessionId]

/**
 * Vietnamese label for each `processing.step` (spec §8.2). Exported for the
 * chat panel's indicator composition.
 */
export const STEP_LABEL: Record<ProcessingStep, string> = {
  find_documents: "Tìm kiếm tài liệu",
  read_content: "Đọc nội dung",
  synthesize_documents: "Tổng hợp tài liệu",
  aggregate: "Gộp tài liệu",
  synthesize_answer: "Tổng hợp kết quả",
}

/**
 * The progress triple the UI labels: the coarse `phase`, the running stage's
 * `steps` (each `{capability, goal}`; empty outside a `processing` stage), and
 * whether the current phase has stalled (`isLongWait`, driven by the 20s
 * `heartbeat` event). Parallel steps in a stage share one `processing` event,
 * so `steps` holds them together.
 */
export type ProgressState = {
  phase: AskStreamPhase | null
  steps: ProcessingStepInfo[]
  isLongWait: boolean
}

export const INITIAL_PROGRESS: ProgressState = {
  phase: "intent_routing",
  steps: [],
  isLongWait: false,
}

/**
 * Pure reducer for the progress triple. Terminal events (`done`/`error`) and
 * token accumulation (`answer`'s streamed text) are handled by the hook; this
 * covers only `{phase, steps, isLongWait}`:
 * - a phase event (intent_routing/planning/review/narrow_down_kbs) sets that
 *   phase and clears steps + isLongWait;
 * - `processing` sets phase=processing + the stage's steps, clears isLongWait
 *   (each turn's stages just replace the prior stage — current turn only);
 * - `heartbeat` only raises isLongWait, keeping phase/steps (the current label
 *   gets a "(vẫn đang xử lý)" hint);
 * - `answer` moves to the answering phase, clearing steps + isLongWait.
 */
export const applyEvent = (
  state: ProgressState,
  event: AskStreamEvent,
): ProgressState => {
  switch (event.type) {
    case "intent_routing":
    case "planning":
    case "review":
    case "narrow_down_kbs":
      return { phase: event.type, steps: [], isLongWait: false }
    case "processing":
      return { phase: "processing", steps: event.steps, isLongWait: false }
    case "heartbeat":
      return { ...state, isLongWait: true }
    case "answer":
      return { phase: "answering", steps: [], isLongWait: false }
    default:
      // done / error — terminal, handled by the hook before applyEvent runs.
      return state
  }
}

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === "AbortError"

export type AskStreamStartArgs = {
  sessionId: string
  content: string
  /**
   * Conversation up to and including the just-sent user message. Used as the
   * base when committing the final assistant reply to the React Query cache.
   */
  sessionForMerge: SynthesisSession
  onDone?: (session: SynthesisSession, result: SynthesisAskResult) => void
  onError?: (error: Error) => void
}

export type UseAskStreamSynthesisSessionResult = {
  start: (args: AskStreamStartArgs) => void
  cancel: () => void
  isStreaming: boolean
  phase: AskStreamPhase | null
  streamedText: string
  /** The running stage's steps (each `{capability, goal}`); empty outside a
   * processing phase. Parallel steps in a stage appear together. */
  steps: ProcessingStepInfo[]
  /** True once the server sends a `heartbeat`, i.e. the current phase has
   * stalled (>20s). Reset on the next real event. */
  isLongWait: boolean
}

export const useAskStreamSynthesisSession =
  (): UseAskStreamSynthesisSessionResult => {
    const qc = useQueryClient()
    const [isStreaming, setIsStreaming] = useState(false)
    const [phase, setPhase] = useState<AskStreamPhase | null>(null)
    const [streamedText, setStreamedText] = useState("")
    const [steps, setSteps] = useState<ProcessingStepInfo[]>([])
    const [isLongWait, setIsLongWait] = useState(false)
    const abortRef = useRef<AbortController | null>(null)

    const start = useCallback(
      ({
        sessionId,
        content,
        sessionForMerge,
        onDone,
        onError,
      }: AskStreamStartArgs) => {
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller

        setIsStreaming(true)
        setPhase(INITIAL_PROGRESS.phase)
        setStreamedText("")
        setSteps(INITIAL_PROGRESS.steps)
        setIsLongWait(INITIAL_PROGRESS.isLongWait)

        let accumulated = ""
        // Tracked locally (like `accumulated`) so applyEvent sees the current
        // progress without a stale-closure read of the useState values.
        let progress: ProgressState = INITIAL_PROGRESS

        const run = async () => {
          try {
            for await (const event of askStreamApi(
              sessionId,
              content,
              controller.signal,
            )) {
              if (controller.signal.aborted) return
              if (event.type === "done") {
                const updated: SynthesisSession = {
                  ...sessionForMerge,
                  messages: [
                    ...sessionForMerge.messages,
                    {
                      id: `assistant-${Date.now()}`,
                      role: "assistant",
                      content: event.result.answer,
                      references: event.result.references,
                      followUpQuestions: event.result.followUpQuestions,
                      consulted: event.result.consulted,
                    },
                  ],
                }
                qc.setQueryData(sessionKey(sessionId), updated)
                onDone?.(updated, event.result)
                return
              }
              if (event.type === "error") {
                throw new Error(event.message)
              }

              progress = applyEvent(progress, event)
              setPhase(progress.phase)
              setSteps(progress.steps)
              setIsLongWait(progress.isLongWait)

              if (event.type === "answer") {
                accumulated += event.delta
                setStreamedText(accumulated)
              }
            }
          } catch (err) {
            if (controller.signal.aborted || isAbortError(err)) return
            onError?.(err instanceof Error ? err : new Error(String(err)))
          } finally {
            if (abortRef.current === controller) {
              abortRef.current = null
            }
            setIsStreaming(false)
            setPhase(null)
            setStreamedText("")
            setSteps([])
            setIsLongWait(false)
          }
        }

        void run()
      },
      [qc],
    )

    const cancel = useCallback(() => {
      abortRef.current?.abort()
      abortRef.current = null
      setIsStreaming(false)
      setPhase(null)
      setStreamedText("")
      setSteps([])
      setIsLongWait(false)
    }, [])

    return { start, cancel, isStreaming, phase, streamedText, steps, isLongWait }
  }
